import React from 'react';
import { IconAlert, IconInfo } from './BuilderUiIcons';
import BrewingInstructionsSection from './BrewingInstructionsSection';
import {
  BlendNameField,
  CartButton,
  CartStatusMessages,
  useBlendCartAction,
} from './AddToCartButton';
import { formatBlendCaffeineLabel } from '../utils/caffeineLabel';
import './BlendPreview.css';

/** Strip legacy emoji prefixes from API strings (older deployments). */
function stripLegacyWarningPrefix(text) {
  const s = String(text ?? '').trim();
  if (s.startsWith('⚠️')) return s.slice(2).trimStart();
  if (s.startsWith('ℹ️')) return s.slice(2).trimStart();
  return s;
}

function InfeasiblePreview({ warnings }) {
  return (
    <section className="content-section blend-adjust-section">
      <div className="infeasible-message">
        <div className="infeasible-icon" aria-hidden>
          <IconAlert className="infeasible-icon-svg" />
        </div>
        <h2 className="infeasible-title">{"Let's adjust your blend"}</h2>
        <div className="infeasible-content">
          {warnings && warnings.length > 0 && (
            <div className="infeasible-explanation">
              {warnings[0].split('\n').map((line, index) => {
                const trimmed = line.trim();
                if (!trimmed) return null;
                if (trimmed.includes('⚠️') && trimmed.includes('Blend Not Feasible')) {
                  return null;
                }
                if (trimmed.includes('**')) {
                  const parts = trimmed.split('**');
                  return (
                    <p key={index}>
                      {parts.map((part, i) =>
                        (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
                    </p>
                  );
                }
                if (trimmed.startsWith('•')) {
                  return <li key={index}>{trimmed.substring(1).trim()}</li>;
                }
                if (trimmed.startsWith('What to try:') || trimmed.startsWith('💡')) {
                  return (
                    <p key={index} className="suggestion">
                      {trimmed.startsWith('💡')
                        ? trimmed.replace(/^💡\s*/, '')
                        : trimmed}
                    </p>
                  );
                }
                return <p key={index}>{trimmed}</p>;
              })}
            </div>
          )}
        </div>
        <p className="infeasible-help">
          Unsure where to start? Pick a botanical with a fuller mark, then add accents.
        </p>
      </div>
    </section>
  );
}

/**
 * Successful blend: offer + ritual (+ notes).
 * Mobile: name in offer, ritual next, sticky Add to Cart at bottom.
 * Desktop: offer column keeps inline ATC; sticky bar hidden.
 */
function BlendPreview({
  result,
  packetSize,
  selectedIngredients,
  preparationMethod,
  ingredients,
  mode,
  customPercentages,
}) {
  const cart = useBlendCartAction({
    blendResult: result,
    packetSize,
    selectedIngredients,
    preparationMethod,
    ingredients,
    mode,
    customPercentages,
  });

  if (!result) return null;

  const {
    success,
    price,
    currency_symbol,
    currency_code,
    caffeine_mg,
    caffeine_level,
    warnings,
    brewing_instructions,
  } = result;

  if (!success) {
    return <InfeasiblePreview warnings={warnings} />;
  }

  const pricePrefix = currency_symbol != null ? currency_symbol : '$';
  const priceSuffix = currency_code ? ` ${currency_code}` : '';
  const caffeineLabel = formatBlendCaffeineLabel(caffeine_level, caffeine_mg);
  const priceDisplay = `${pricePrefix}${price.toFixed(2)}`;

  return (
    <div className="blend-result-stack">
      <div
        className={
          brewing_instructions
            ? 'blend-result-duo'
            : 'blend-result-duo blend-result-duo--offer-only'
        }
      >
        <section className="content-section blend-offer-section">
          <p className="blend-offer__eyebrow">Your blend</p>
          <h2 className="blend-offer__title">Your Custom Blend</h2>
          <p className="blend-offer__price">
            <span className="blend-offer__price-amount">{priceDisplay}</span>
            {priceSuffix && (
              <span className="blend-offer__price-currency">{priceSuffix.trim()}</span>
            )}
          </p>
          <p className="blend-offer__meta">
            <span>{packetSize}g pouch</span>
            <span className="blend-offer__meta-dot" aria-hidden="true">·</span>
            <span>{caffeineLabel}</span>
          </p>

          <div className="blend-offer__purchase">
            <BlendNameField cart={cart} />
            <div className="blend-offer__atc-desktop">
              <CartButton cart={cart} />
              <CartStatusMessages cart={cart} />
            </div>
          </div>
        </section>

        {brewing_instructions && (
          <section className="content-section blend-ritual-section">
            <BrewingInstructionsSection brewingInstructions={brewing_instructions} />
          </section>
        )}
      </div>

      {warnings && warnings.length > 0 && (
        <section className="content-section blend-notes-section">
          <p className="blend-notes__heading">
            <IconInfo className="blend-notes__heading-icon" aria-hidden />
            Please note
          </p>
          <ul className="blend-notes__list">
            {warnings.map((warning, index) => (
              <li key={index}>{stripLegacyWarningPrefix(warning)}</li>
            ))}
          </ul>
        </section>
      )}

      <div
        className="blend-sticky-cart"
        role="region"
        aria-label="Add your blend to cart"
      >
        <div className="blend-sticky-cart__meta">
          <span className="blend-sticky-cart__label">Your blend</span>
          <span className="blend-sticky-cart__price">
            {priceDisplay}
            {priceSuffix.trim() ? (
              <span className="blend-sticky-cart__currency">{priceSuffix.trim()}</span>
            ) : null}
          </span>
        </div>
        <CartButton cart={cart} className="blend-sticky-cart__btn" />
        <div className="blend-sticky-cart__status">
          <CartStatusMessages cart={cart} />
        </div>
      </div>
    </div>
  );
}

export default BlendPreview;
